import React, { useMemo } from 'react';
import { decode } from 'utils/crypto/crypto';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { translate } from 'components/Translation';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import SimpleTable from 'components/SimpleTable/SimpleTable';
import { CommitmentDetails } from 'utils/api/models/payments/commitmentDetails';
import moment from 'moment/moment';
import { TextCell, HeaderCell } from 'components/Cells/Cells';
import { SingleCommitmentDetails } from 'utils/api/models/payments/singleCommitmentDetails';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/activeCommitmentsStyle';
import { makeStyles, Tooltip } from '@material-ui/core';
import SummaryCard from './subcomponents/SummaryCard';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';
import { useSelector } from 'react-redux';
import { upIdSelector, userIdSelector } from 'redux/modules/session/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

const ACTIVE = 'Active';

interface ActiveCommitmentsProps {
	commitmentDetails: CommitmentDetails[];
	match: {
		path: string;
		params: {
			phoneNumberEncoded: string;
			upAccountId: string;
			accountNumber: string;
		};
	};
	accountName: string;
}

const ActiveCommitments = ({ commitmentDetails, match, accountName }: ActiveCommitmentsProps) => {
	const { phoneNumberEncoded } = match.params;
	const phoneNumber: string = decode(phoneNumberEncoded);
	const classes = useStyles();
	const accId = useSelector(upIdSelector);
	const userId = useSelector(userIdSelector);
	const filteredCommitmentDetailsItems = useMemo(
		() =>
			commitmentDetails
				.map(({ commitmentDetails }) => commitmentDetails)
				.flat(1)
				.filter(({ status, commitmentPenaltyAmount }) => status === ACTIVE && commitmentPenaltyAmount > 0),
		[commitmentDetails]
	);

	const totalCommitmentValue = useMemo(() => {
		return (
			commitmentDetails
				.map(({ sumOfActiveCommitmentPenaltyAmount }) => sumOfActiveCommitmentPenaltyAmount)
				.reduce((prevValue, currentValue) => prevValue + currentValue, 0) || 0
		);
	}, [commitmentDetails]);

	React.useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: `My Numbers: Active Commitments`,
				accountID: accId,
				billingID: match.params.accountNumber,
				accountNumber: phoneNumber,
				userId: userId,
				language: getLanguage(),
			},
		});
	}, []);

	return (
		<GridContainer justify="flex-start">
			<BreadcrumbsItem to={match.path}>{`${phoneNumber} (${accountName}) - ${translate(
				'path.active.commitments'
			)}`}</BreadcrumbsItem>
			<GridItem xs={12}>
				<>
					<SimpleTable
						idAccessor="accountCode"
						className={classes.simpleTable}
						columns={[
							{
								accessor: 'name',
								cell: (row: SingleCommitmentDetails) => (
									<Tooltip title={row.name} arrow placement="right">
										<div>
											<TextCell text={row.name} bold color="primary-light" maxWidth={200} />
										</div>
									</Tooltip>
								),
								header: <HeaderCell text={translate('active.commitments.name')} />,
								width: 250,
							},
							{
								accessor: 'totalPeriods',
								cell: (row: SingleCommitmentDetails) => (
									<TextCell text={row.totalPeriods} color="primary-light" />
								),
								header: <HeaderCell text={translate('active.commitments.total.months')} />,
								width: 14,
								widthPercentage: true,
							},
							{
								accessor: 'pendingPeriods',
								cell: (row: SingleCommitmentDetails) => (
									<TextCell text={row.pendingPeriods} color="primary-light" />
								),
								header: <HeaderCell text={translate('active.commitments.remaining.months')} />,
								width: 14,
								widthPercentage: true,
							},
							{
								accessor: 'startDate',
								cell: (row: SingleCommitmentDetails) => (
									<TextCell text={moment(row.startDate).format('DD/MM/YYYY')} color="primary-light" />
								),
								header: <HeaderCell text={translate('active.commitments.start.date')} />,
								width: 14,
								widthPercentage: true,
							},
							{
								accessor: 'endDate',
								cell: (row: SingleCommitmentDetails) => (
									<TextCell text={moment(row.endDate).format('DD/MM/YYYY')} color="primary-light" />
								),
								header: <HeaderCell text={translate('active.commitments.end.date')} />,
								width: 14,
								widthPercentage: true,
							},
							{
								accessor: 'commitmentPenaltyAmount',
								cell: (row: SingleCommitmentDetails) => (
									<TextCell
										text={translate('price', row.commitmentPenaltyAmount)}
										bold
										color="primary-light"
									/>
								),
								header: <HeaderCell text={translate('active.commitments.penalty')} />,
								width: 14,
								widthPercentage: true,
							},
						]}
						data={filteredCommitmentDetailsItems}
						selectedItems={[]}
						setSelectedItems={() => {}}
					/>
					<SummaryCard value={totalCommitmentValue} />
				</>
			</GridItem>
		</GridContainer>
	);
};

export default ActiveCommitments;
